import {
  accessLevel,
  ago,
  type Config,
  can,
  canAttachToTicket,
  canCommentOnTicket,
  canReopenTicket,
  etaViewerFor,
  fmtDateTime,
  stageOf,
  type TicketDetail,
} from '@hrms/shared'
import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { LuEye, LuInfo, LuLock, LuRotateCcw, LuSearchX, LuX } from 'react-icons/lu'
import { useCurrentUser } from '../../../auth/AuthProvider.tsx'
import {
  Button,
  EmptyState,
  IconButton,
  useDialogFocus,
  useToast,
} from '../../../components/ui/index.ts'
import { ApiError } from '../../../lib/api.ts'
import { cn } from '../../../lib/cn.ts'
import { useConfig } from '../../../lib/config.ts'
import { hint } from '../../../lib/hint.ts'
import { useNow } from '../../../lib/useNow.ts'
import { ActivityList } from './Activity.tsx'
import { TicketAttachments } from './Attachments.tsx'
import { ticketKeyOf, useReopenTicket, useTicket } from './api.ts'
import { CommentThread, Composer } from './Conversation.tsx'
import { HrSidePanel } from './HrSidePanel.tsx'
import {
  CategoryChip,
  EtaText,
  LockIcon,
  PersonAvatar,
  PriorityBars,
  personName,
  StatusLozenge,
} from './ui.tsx'

/** Right-side ticket panel (DESIGN.md §3.5). URL-driven; see `useTicketParam`. */
export function TicketDrawer({ ticketKey, onClose }: { ticketKey: string; onClose: () => void }) {
  const user = useCurrentUser()
  const ref = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)
  useDialogFocus(ref, onClose, closeRef)
  const detail = useTicket(ticketKey)
  const { data: config } = useConfig()
  const notFound = detail.error instanceof ApiError && detail.error.status === 404
  const toast = useToast()
  const qc = useQueryClient()

  // PRD "Edge cases": a ticket that becomes hidden while open (e.g. a confidential ticket
  // reassigned to someone else) closes the panel with a message.
  const loaded = useRef(false)
  if (detail.data) loaded.current = true
  const loseAccess = useCallback(() => {
    toast(`You no longer have access to ${ticketKey.toUpperCase()}, so it was closed.`)
    qc.removeQueries({ queryKey: ticketKeyOf(ticketKey) })
    onClose()
  }, [toast, qc, ticketKey, onClose])
  useEffect(() => {
    if (notFound && loaded.current) loseAccess()
  }, [notFound, loseAccess])

  const header = detail.data && config ? detail.data.ticket : null
  return createPortal(
    <div className="fixed inset-0 z-50">
      <div aria-hidden className="absolute inset-0 animate-fade bg-scrim" onClick={onClose} />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={header ? `${header.key}: ${header.title}` : `Ticket ${ticketKey}`}
        className="absolute inset-y-0 right-0 flex w-[min(980px,100%)] animate-slide flex-col bg-surface pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] shadow-pop"
      >
        <div className="flex h-13 shrink-0 items-center gap-2.5 border-b border-border px-5 max-[720px]:px-3.5">
          <span className="tabular font-semibold">{header?.key ?? ticketKey.toUpperCase()}</span>
          {header && config && (
            <CategoryChip categoryId={header.categoryId} categories={config.categories} />
          )}
          {header?.confidential && <LockIcon employee={accessLevel(user) === 'employee'} />}
          <IconButton
            ref={closeRef}
            aria-label="Close"
            className="ml-auto"
            onClick={onClose}
            {...hint('Close (Esc)')}
          >
            <LuX />
          </IconButton>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {notFound ? (
            <div className="p-6">
              <EmptyState icon={LuSearchX} color="slate" title="Ticket not found">
                This ticket doesn’t exist, or you don’t have access to it.
              </EmptyState>
            </div>
          ) : detail.isError ? (
            <div className="p-6">
              <EmptyState icon={LuInfo} color="amber" title="Couldn’t load this ticket">
                Check your connection and try again.
              </EmptyState>
            </div>
          ) : !detail.data || !config ? (
            <p className="p-6 text-muted" aria-live="polite">
              Loading ticket…
            </p>
          ) : (
            <TicketBody detail={detail.data} config={config} onLostAccess={loseAccess} />
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}

type Tab = 'comments' | 'activity'

function TicketBody({
  detail,
  config,
  onLostAccess,
}: {
  detail: TicketDetail
  config: Config
  onLostAccess: () => void
}) {
  const user = useCurrentUser()
  const now = useNow()
  const [tab, setTab] = useState<Tab>('comments')
  const { ticket, comments, activity, attachments, people } = detail
  const toast = useToast()
  const level = accessLevel(user)
  const hrView = level !== 'employee' && user.id !== ticket.raisedBy
  const raiser = people[ticket.raisedBy]
  const logger = ticket.loggedBy !== ticket.raisedBy ? people[ticket.loggedBy] : undefined
  const stage = stageOf(config.statuses, ticket.statusId)
  const canComment = canCommentOnTicket(user, ticket)
  const editable = can(user, 'ticket.edit.fields')
  const firstName = (raiser?.name ?? 'the employee').split(' ')[0] ?? 'the employee'

  return (
    <div className="grid min-h-full grid-cols-[1fr_300px] max-[980px]:grid-cols-1">
      <section className="min-w-0 px-7 pt-[22px] pb-10 max-[720px]:px-3.5">
        <h2 className="text-[21px] leading-snug break-words">{ticket.title}</h2>
        <p className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] text-muted">
          <PersonAvatar person={raiser} id={ticket.raisedBy} />
          <span>
            Raised by{' '}
            <strong className="font-semibold text-text">
              {personName(people, ticket.raisedBy)}
            </strong>
            {raiser?.dept && `, ${raiser.dept}`}
          </span>
          <span>on {fmtDateTime(ticket.createdAt, { now })}</span>
          {logger && <span>(logged by {logger.name})</span>}
        </p>
        <p className="mt-4 break-words whitespace-pre-wrap">
          {ticket.description || <span className="text-muted">No description.</span>}
        </p>
        <TicketAttachments
          ticketKey={ticket.key}
          attachments={attachments}
          canAttach={canAttachToTicket(user, ticket)}
          onUploaded={(res) =>
            toast(
              res.attachments.length === 1
                ? `${res.attachments[0]?.name ?? 'File'} attached`
                : `${res.attachments.length} files attached`,
            )
          }
        />

        <div
          role="tablist"
          aria-label="Ticket sections"
          className="mt-6 flex gap-1 border-b border-border"
        >
          {(
            [
              ['comments', `${hrView ? 'Conversation' : 'Comments'} (${comments.length})`],
              ['activity', 'Activity'],
            ] as const
          ).map(([id, label]) => (
            <button
              key={id}
              type="button"
              role="tab"
              id={`tab-${id}`}
              aria-selected={tab === id}
              aria-controls={`panel-${id}`}
              onClick={() => setTab(id)}
              className={cn(
                '-mb-px border-b-2 px-3 py-2 font-medium transition-colors',
                tab === id
                  ? 'border-accent text-text'
                  : 'border-transparent text-muted hover:text-text',
              )}
            >
              {label}
            </button>
          ))}
        </div>
        <div
          role="tabpanel"
          id={`panel-${tab}`}
          aria-labelledby={`tab-${tab}`}
          className="pt-4"
          data-tour="ticket-comments"
        >
          {tab === 'comments' ? (
            <>
              <CommentThread comments={comments} people={people} now={now} hrView={hrView} />
              {canComment ? (
                <Composer
                  key={ticket.key}
                  ticketKey={ticket.key}
                  hrView={hrView}
                  canNote={hrView && can(user, 'ticket.note.internal')}
                  raiserFirstName={firstName}
                />
              ) : (
                level === 'viewer' && (
                  <p
                    data-tour="viewer-note"
                    className="mt-5 flex items-center gap-1.5 rounded-card border border-border bg-surface-2 px-3 py-2.5 text-[13px] text-muted"
                  >
                    <LuEye aria-hidden className="size-4 shrink-0 text-blue-fg" />
                    View-only access: you can read this ticket but not reply or change it.
                  </p>
                )
              )}
            </>
          ) : (
            <ActivityList
              activity={activity}
              attachments={attachments}
              people={people}
              config={config}
              ticket={ticket}
              now={now}
            />
          )}
        </div>
      </section>

      <aside
        aria-label="Ticket details"
        data-tour="ticket-side"
        className="border-l border-border bg-surface-2 px-5 py-[22px] max-[980px]:order-first max-[980px]:border-b max-[980px]:border-l-0 max-[720px]:px-3.5"
      >
        {editable ? (
          <HrSidePanel detail={detail} config={config} now={now} onLostAccess={onLostAccess} />
        ) : (
          <SidePanel detail={detail} config={config} hrView={hrView} stage={stage} now={now} />
        )}
      </aside>
    </div>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-4 max-[980px]:mb-0">
      <p className="mb-[5px] text-[12.5px] font-medium text-muted">{label}</p>
      <div className="flex min-h-6 items-center gap-2">{children}</div>
    </div>
  )
}

function Callout({
  children,
  warning,
  tour,
}: {
  children: ReactNode
  warning?: boolean
  tour?: string
}) {
  return (
    <div
      data-tour={tour}
      className={cn(
        'mt-3 rounded-card border px-3 py-2.5 text-[13px]',
        warning ? 'border-note-border bg-note-bg text-text' : 'border-border bg-surface text-muted',
      )}
    >
      {children}
    </div>
  )
}

/** Read-only side panel: employees, and viewers (with HR wording). */
function SidePanel({
  detail,
  config,
  hrView,
  stage,
  now,
}: {
  detail: TicketDetail
  config: Config
  hrView: boolean
  stage: ReturnType<typeof stageOf>
  now: number
}) {
  const user = useCurrentUser()
  const toast = useToast()
  const { ticket, people } = detail
  const reopenMut = useReopenTicket(ticket.key)
  const assignee = ticket.assignee ? people[ticket.assignee] : undefined
  const viewer = etaViewerFor(hrView ? accessLevel(user) : 'employee')

  const reopen = async () => {
    try {
      await reopenMut.mutateAsync(ticket.version)
      toast(`${ticket.key} reopened`)
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Couldn’t save that change. Try again.')
    }
  }

  return (
    <>
      <div className="grid grid-cols-1 max-[980px]:grid-cols-2 max-[980px]:gap-x-4 max-[980px]:gap-y-3">
        <Row label="Status">
          <StatusLozenge statusId={ticket.statusId} statuses={config.statuses} />
        </Row>
        <Row label={hrView ? 'Assignee' : 'Handled by'}>
          {ticket.assignee ? (
            <>
              <PersonAvatar person={assignee} id={ticket.assignee} />
              <span className="min-w-0 truncate">{personName(people, ticket.assignee)}</span>
            </>
          ) : (
            <span className="text-muted">{hrView ? 'Unassigned' : 'Not picked up yet'}</span>
          )}
        </Row>
        <Row label="Priority">
          <PriorityBars priorityId={ticket.priorityId} priorities={config.priorities} />
        </Row>
        <Row label={hrView ? 'ETA' : 'Expected resolution'}>
          <EtaText ticket={ticket} config={config} viewer={viewer} now={now} />
        </Row>
      </div>

      {ticket.confidential && (
        <Callout>
          <span className="flex gap-2">
            <LuLock aria-hidden className="mt-0.5 size-3.5 shrink-0 text-amber-fg" />
            <span>
              {hrView
                ? 'Confidential. Only HR leads and the assignee can see this ticket.'
                : 'Confidential. Only HR leads and your assigned HR partner can see this ticket.'}
            </span>
          </span>
        </Callout>
      )}
      {stage === 'waiting' && !hrView && (
        <Callout warning>
          HR is waiting for more information from you. Reply in the comments below and the ticket
          goes straight back to HR.
        </Callout>
      )}
      {stage === 'done' && canReopenTicket(user, ticket) && (
        <Callout tour="ticket-reopen">
          <p>Still not sorted? Reopen the ticket and HR will pick it up again.</p>
          <Button
            size="sm"
            className="mt-2"
            onClick={() => void reopen()}
            disabled={reopenMut.isPending}
            {...hint('Moves the ticket back to HR with a fresh expected date')}
          >
            <LuRotateCcw aria-hidden className="size-3.5 text-orange-fg" />
            Reopen ticket
          </Button>
        </Callout>
      )}

      <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 border-t border-border pt-3.5 text-[13px]">
        <dt className="text-muted">Created</dt>
        <dd className="text-right" {...hint(fmtDateTime(ticket.createdAt, { now }))}>
          {ago(ticket.createdAt, { now })}
        </dd>
        <dt className="text-muted">Updated</dt>
        <dd className="text-right" {...hint(fmtDateTime(ticket.updatedAt, { now }))}>
          {ago(ticket.updatedAt, { now })}
        </dd>
        {ticket.resolvedAt !== null && (
          <>
            <dt className="text-muted">Resolved</dt>
            <dd className="text-right">{fmtDateTime(ticket.resolvedAt, { now })}</dd>
          </>
        )}
      </dl>
    </>
  )
}
